// Quelles actions proposer sur une connexion Tradovate ?
//
// ─────────────────────────────────────────────────────────────────────────────
// PLUS AUCUNE SYNCHRO MANUELLE (décision produit, 2026-09-23).
//
// « Synchroniser maintenant » avait déjà été retiré. « Réimporter les trades
// récents » l'est à son tour, pour la même raison poussée à son terme : il
// invitait à cliquer en boucle. Un utilisateur voyant des totaux partiels —
// sync initiale en cours, ou cycle bridé par le quota — cliquait, ne
// constatait aucun changement immédiat, recliquait, puis ouvrait un ticket.
//
// Et le geste AGGRAVAIT ce qu'il prétendait réparer : le full resync est le
// poste le plus lourd du quota Tradovate (2 GET `/fillFee/deps` par
// round-trip, contre 600 req/h par IP PARTAGÉE entre tous les utilisateurs).
// Chaque clic dégradait donc la synchro de tout le monde.
//
// La collecte est entièrement pilotée côté serveur : cron toutes les 15 min,
// plus le socket temps réel. « Reconnecter » est la seule action conservée,
// parce qu'elle répond au seul besoin que le serveur NE PEUT PAS couvrir seul :
// re-autoriser l'OAuth exige une action de l'utilisateur.
//
// ⚠️ L'action serveur `fullResyncTradovate` RESTE en place (main.wasp), comme
// `syncTradovateNow` avant elle : la retirer casserait les clients encore
// chargés en mémoire, et elle conserve ses propres plafonds (1/h par
// connexion, 3/h par utilisateur). Seul le bouton disparaît.
// ─────────────────────────────────────────────────────────────────────────────
//
// ─────────────────────────────────────────────────────────────────────────────
// POURQUOI UNE INTERRUPTION DU TEMPS REEL EFFACE LES AUTRES ACTIONS.
//
// `tradovateRealtimeStatus.ts` sépare délibérément deux axes : le statut de
// sync dit « mes trades remontent-ils ? », le temps réel dit « à quelle
// vitesse ». Cette séparation reste vraie pour les BADGES — un utilisateur dont
// le socket est coupé continue d'être synchronisé par le REST.
//
// Mais elle ne dicte pas les ACTIONS. Face à « Connexion temps réel
// interrompue », l'utilisateur cherche à rétablir sa connexion. Lui présenter
// « Réimporter les trades récents » et « Déconnecter » l'oriente vers deux
// gestes qui ne répondent pas à ce que le badge vient de lui annoncer : le
// réimport relit les mêmes données par le même transport dégradé, et la
// déconnexion est l'inverse de ce qu'il veut.
//
// Une barre d'actions répond à « que dois-je faire maintenant ? ». Quand il n'y
// a qu'une bonne réponse, en afficher trois est un coût, pas un service.
//
// ⚠️ « Déconnecter » est retiré de la BARRE, jamais du composant. Le supprimer
// vraiment enfermerait l'utilisateur : une connexion durablement interrompue
// deviendrait impossible à supprimer. Il part en débordement, et un test
// structurel interdit qu'aucun chemin de déconnexion ne subsiste.
// ─────────────────────────────────────────────────────────────────────────────
//
// MODULE PUR : aucun import `wasp/*` ni React, pour que la règle soit testable
// sans monter le composant.

export interface SyncActionsInput {
  /** Statut de sync affiché (`displayStatus`, replié sur `status`). */
  displayStatus: string | undefined;
  /** État du flux temps réel (cf. `RealtimeStatus`). */
  realtimeStatus: string | undefined;
}

export interface SyncActions {
  /**
   * « Réimporter les trades récents » (full resync).
   *
   * TOUJOURS `false` depuis le 2026-09-23 : plus aucune synchro manuelle n'est
   * proposée (cf. l'en-tête de ce module). Le champ est CONSERVÉ pour ne pas
   * casser les appelants, et un test structurel garantit qu'aucun statut ne
   * peut le rallumer.
   */
  fullResync: boolean;
  /** « Reconnecter » (redirection OAuth). */
  reconnect: boolean;
  /** « Déconnecter », dans la barre d'actions. */
  disconnect: boolean;
  /** « Déconnecter », replié dans le menu de débordement. */
  overflowDisconnect: boolean;
}

/** Statuts de sync qui appellent d'eux-mêmes une reconnexion. */
const RECONNECTABLE = new Set(["needs_reauth", "idle", "foreign_token"]);

/**
 * Résout les actions visibles pour une connexion.
 *
 * Une interruption DURABLE du temps réel (`interrupted`, soit plus de 30 min
 * sans bail renouvelé) écrase les autres actions : seule la reconnexion reste.
 * `reconnecting` ne déclenche rien — c'est une coupure brève qui se rétablit
 * seule, et alarmer dessus est exactement ce que le seuil de 30 min évite.
 */
export function resolveSyncActions(input: SyncActionsInput): SyncActions {
  const status = input.displayStatus ?? "";
  const realtimeInterrupted = input.realtimeStatus === "interrupted";

  if (realtimeInterrupted) {
    return {
      fullResync: false,
      reconnect: true,
      disconnect: false,
      // Le seul chemin de sortie restant : sans lui, la connexion serait
      // impossible à supprimer tant que le temps réel ne revient pas.
      overflowDisconnect: true,
    };
  }

  return {
    // Aucune synchro manuelle : la collecte est entièrement pilotée côté
    // serveur (cron 15 min + socket). Cf. l'en-tête de ce module.
    fullResync: false,
    reconnect: RECONNECTABLE.has(status),
    disconnect: true,
    overflowDisconnect: false,
  };
}
